import React, { useState, useMemo, useEffect, useCallback } from "react"; // Added useCallback
import { useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import InputForm from "../components/homePageComponents/inputForm"; // Capital 'I'
import { loadApCollegesYear, loadTsCollegesYear, loadTsPharmacyColleges, loadApPharmacyColleges, logCollegeSearchSubmission } from "../api/getApi"; // Add logCollegeSearchSubmission
import { getRankKey, classifyRisk } from "../actions/actions";
import DesktopResults from "../components/homePageComponents/desktopResults";
import MobileResults from "../components/homePageComponents/mobileResults";
import WhyUs from "../components/whyus";
import Text from "../components/text"; // Import the Text component
import MobileFilterDrawer from "../components/mobileFilterDrawer";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/authContext";
import OtherLinks from "../components/homePageComponents/otherLinks";

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Added
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    rank: "",
    gender: "",
    caste: "",
    examType: "",
    year: "2023",
    location: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [loading, setLoading] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [error, setError] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [isInfoVisible, setIsInfoVisible] = useState(true); // State for info alert
  const [showPlaceDrawer, setShowPlaceDrawer] = useState(false);
  const [showBranchDrawer, setShowBranchDrawer] = useState(false);

  // Handle form submission (to be passed to InputForm)
  const handleFormSubmit = useCallback(async (formDataFromChild) => {
    setError("");
    // Update local formData state if needed, or rely on it being passed if this is an auto-submit
    // For clarity, let's assume formDataFromChild is the complete and correct data for this submission.
    setFormData(formDataFromChild);


    if (
      !formDataFromChild.rank ||
      formDataFromChild.rank < 1 ||
      formDataFromChild.rank > 500000
    ) {
      setError("Please enter a valid rank between 1 and 500000.");
      setLoading(false); // Ensure loading is stopped
      return;
    }

    if (!formDataFromChild.gender || !formDataFromChild.caste || !formDataFromChild.examType || !formDataFromChild.year || !formDataFromChild.location) {
      setError("Please fill in all required fields.");
      setLoading(false); // Ensure loading is stopped
      return;
    }

    if (!isAuthenticated) {
      navigate("/signup", {
        state: {
          // Pass the form data that needs to be submitted after auth
          pendingAction: {
            type: "submitCollegeSearch",
            data: formDataFromChild,
          },
          redirectAfterLogin: "/", // Ensure redirection back to home
        },
      });
      return;
    }

    setLoading(true);

    try {
      let response;
      if (formDataFromChild.examType === "ap") {
        response = await loadApCollegesYear(formDataFromChild);
      } else if (formDataFromChild.examType === "ap_bipc") {
        response = await loadApPharmacyColleges(formDataFromChild);
      } else if (formDataFromChild.examType === "ts_bipc") {
        response = await loadTsPharmacyColleges(formDataFromChild);
      } else {
        response = await loadTsCollegesYear(formDataFromChild);
      }

      if (!response.success) {
        setError(response.error || "Failed to load colleges. Please try again.");
        setColleges([]); // Clear previous results on error
        setLoading(false);
        return;
      }

      setColleges(response.data || []);
      setFormSubmitted(true);
      window.scrollTo({
        top: window.scrollY + 500, // Adjust scroll as needed
        behavior: "smooth",
      });

      if (isAuthenticated) {
        logCollegeSearchSubmission(formDataFromChild);
      }
    } catch (error) {
      console.error("Error loading college data:", error);
      setError(
        "An error occurred while fetching college data. Please try again."
      );
      setColleges([]); // Clear previous results on error
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, navigate]); // Add dependencies for useCallback

  // Navigate to full filter page for advanced options
  const handleAdvancedSearch = () => {
    // console.log(formData.caste)
    navigate(
      "/filter",
      { state: { inputData: formData } } // Pass formData and colleges to the filter page
    );
  };

  // Get the appropriate rank key based on caste and gender
  const rankKey = getRankKey(formData);

  // Extract unique places and branches when colleges are loaded
  const uniquePlaces = useMemo(() => {
    return [...new Set(colleges.map((college) => college.place))].sort();
  }, [colleges]);

  const uniqueBranches = useMemo(() => {
    return [...new Set(colleges.map((college) => college.branch_code))].sort();
  }, [colleges]);

  // Filter places and branches based on search terms
  const filteredPlaces = useMemo(() => {
    return uniquePlaces.filter(
      (place) =>
        place && place.toLowerCase().includes(placeSearchTerm.toLowerCase())
    );
  }, [uniquePlaces, placeSearchTerm]);

  const filteredBranches = useMemo(() => {
    return uniqueBranches.filter(
      (branch) =>
        branch && branch.toLowerCase().includes(branchSearchTerm.toLowerCase())
    );
  }, [uniqueBranches, branchSearchTerm]);
  // console.log("Unique Places:", uniquePlaces);
  // console.log("Unique Branches:", uniqueBranches);

  // Apply all filters to colleges
  const filteredColleges = useMemo(() => {
    const filtered = colleges.filter((college) => {
      const collegeRankValue = college[rankKey];

      // --- Start of Change: Rank Filtering ---
      // If the college has a numeric rank, apply the filter.
      // If the rank is "-" or "NA", it should pass this filter.
      if (collegeRankValue && /^\d+$/.test(collegeRankValue)) {
        const collegeRank = parseInt(collegeRankValue, 10);
        const userRank = parseInt(formData.rank, 10) || 0;
        const marginRank = Math.floor(userRank * 0.9); // 10% margin below user rank

        if (collegeRank < marginRank) {
          return false; // Filter out if rank is below the safety margin
        }
      }
      // --- End of Change: Rank Filtering ---

      // Filter by search term
      const matchesSearch =
        college.inst_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.inst_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.place.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.branch_code.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by selected places
      const matchesPlace =
        selectedPlaces.length === 0 || selectedPlaces.includes(college.place);

      // Filter by selected branches
      const matchesBranch =
        selectedBranches.length === 0 ||
        selectedBranches.includes(college.branch_code);

      return matchesSearch && matchesPlace && matchesBranch;
    });

    // --- Start of Change: Sorting ---
    // Sort the filtered results to place non-numeric ranks at the end
    return filtered.sort((a, b) => {
      const rankA = a[rankKey];
      const rankB = b[rankKey];

      const isARankless = !/^\d+$/.test(rankA);
      const isBRankless = !/^\d+$/.test(rankB);

      if (isARankless && isBRankless) return 0; // Both are rankless, keep order
      if (isARankless) return 1; // A is rankless, move it to the end
      if (isBRankless) return -1; // B is rankless, move it to the end

      // Both have numeric ranks, sort them
      return parseInt(rankA, 10) - parseInt(rankB, 10);
    });
    // --- End of Change: Sorting ---
  }, [
    colleges,
    searchTerm,
    selectedPlaces,
    selectedBranches,
    formData.rank,
    rankKey,
  ]); // Added formData.rank and rankKey dependencies

  // Handle filter changes
  const handlePlaceFilterChange = (place) => {
    setSelectedPlaces((prev) =>
      prev.includes(place) ? prev.filter((p) => p !== place) : [...prev, place]
    );
  };

  const handleBranchFilterChange = (branch) => {
    setSelectedBranches((prev) =>
      prev.includes(branch)
        ? prev.filter((b) => b !== branch)
        : [...prev, branch]
    );
  };

  // useEffect for auto-submission after login/signup
  useEffect(() => {
    const { formDataForAutoSubmit, autoSubmit, message } = location.state || {};

    if (message) {
        // Potentially display this message to the user (e.g., using a toast notification)
        console.log("Message from auth page:", message);
        // Clear message after displaying
        navigate(location.pathname, { replace: true, state: { ...location.state, message: undefined } });
    }

    if (autoSubmit && formDataForAutoSubmit && isAuthenticated) {
      if (
        formDataForAutoSubmit.rank &&
        formDataForAutoSubmit.gender &&
        formDataForAutoSubmit.caste &&
        formDataForAutoSubmit.examType &&
        formDataForAutoSubmit.year &&
        formDataForAutoSubmit.location
      ) {
        handleFormSubmit(formDataForAutoSubmit);
      } else {
        console.warn(
          "Auto-submit attempted without complete form data:",
          formDataForAutoSubmit
        );
        setError("Could not auto-submit form due to incomplete data from previous step.");
      }
      // Clear the state to prevent re-submission on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, isAuthenticated, navigate, handleFormSubmit]);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Floating Button for Desktop */}
      {/* <div className="hidden md:block">
        <FloatingButton />
      </div> */}

      {/* Floating Button for Mobile */}
      {/* <div className="block md:hidden">
        <MobileFloatingButton />
      </div> */}
      <Helmet>
        <title>{Text.homeTitle} | CollegeRank</title>
        <meta name="description" content={Text.homeDescription} />
      </Helmet>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-6 px-4 shadow-md">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {Text.homeTitle}
          </h1>
          <p className="text-sm text-blue-100 max-w-2xl mx-auto">
            {Text.homeDescription}
          </p>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-8 desktopView ">
          {/* Left Side Sponsor Cards */}
          {/* <div className="md:col-span-1 space-y-6 md:block hidden d-order-2 d-half-width">
            <SponcerCard />
            <SponcerCard />
          </div> */}

          {/* Form Section (Center - takes 2 columns) */}
          <div className="md:col-span-4 space-y-6 d-order-1 d-full-width">
            <InputForm
              onSubmit={handleFormSubmit}
              loading={loading}
              error={error}
              onAdvancedSearch={handleAdvancedSearch}
              // Pass initialFormData if InputForm needs to be controlled by Home's formData state
              // For auto-submit, handleFormSubmit directly receives the data.
              // For manual use, InputForm's internal state or a controlled `initialFormData={formData}` can be used.
              // Let's assume InputForm manages its own state until submission,
              // or if it's controlled, ensure `formData` on Home page is updated correctly.
              // For this flow, `formDataFromChild` in `handleFormSubmit` is key.
            />
            {/* <div className="hidden md:block">
            <SponcerBanner />
            </div> */}
          </div>
          {/* Right Side Sponsor Cards */}
          {/* <div className="md:col-span-1 space-y-6 d-order-3 d-half-width">
            <SponcerCard />
            <SponcerCard />
          </div> */}
        </div>
      </div>

      {/* Results Section */}
      {formSubmitted && (
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-3 sm:space-y-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 sm:h-7 sm:w-7 mr-2 text-blue-600 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span className="truncate">
                  {Text.AvailableColleges} ({filteredColleges.length})
                </span>
              </h2>
              <button
                onClick={handleAdvancedSearch}
                className="w-full sm:w-auto px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm sm:text-base"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-1.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {Text.CreateList}
              </button>
            </div>

            {/* Add information alert about predictions */}
            {isInfoVisible && (
              <div className="bg-blue-50 border-l-4 hidden md:block border-blue-400 p-4 rounded-md mb-6 relative">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">{Text.ImportantNote}</span>
                    </p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setIsInfoVisible(false)}
                      className="inline-flex rounded-md p-1.5 text-blue-600 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <span className="sr-only">Dismiss</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {colleges.length === 0 ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">{Text.NoColleges}</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Search and filter controls */}
                <div className="mb-6 space-y-4">
                  {/* Search bar */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search colleges by name, code, place or branch..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Filter buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setShowPlaceDrawer(true)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 ${
                        selectedPlaces.length > 0
                          ? "bg-blue-50 text-blue-700 border-blue-300"
                          : "bg-white text-gray-700 border-gray-300"
                      }`}
                    >
                      <span>Location</span>
                      {selectedPlaces.length > 0 && (
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {selectedPlaces.length}
                        </span>
                      )}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowBranchDrawer(true)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 ${
                        selectedBranches.length > 0
                          ? "bg-blue-50 text-blue-700 border-blue-300"
                          : "bg-white text-gray-700 border-gray-300"
                      }`}
                    >
                      <span>Branch</span>
                      {selectedBranches.length > 0 && (
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {selectedBranches.length}
                        </span>
                      )}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </button>
                    {(selectedPlaces.length > 0 ||
                      selectedBranches.length > 0) && (
                      <button
                        onClick={() => {
                          setSelectedPlaces([]);
                          setSelectedBranches([]);
                        }}
                        className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>

                  {/* MobileFilterDrawer for Location */}
                  <MobileFilterDrawer
                    isOpen={showPlaceDrawer}
                    onClose={() => setShowPlaceDrawer(false)}
                    title="Select Locations"
                    searchTerm={placeSearchTerm}
                    onSearchChange={(e) => setPlaceSearchTerm(e.target.value)}
                    filterItems={filteredPlaces}
                    selectedItems={selectedPlaces}
                    onItemToggle={handlePlaceFilterChange}
                    searchPlaceholder="Search locations..."
                  />

                  {/* MobileFilterDrawer for Branch */}
                  <MobileFilterDrawer
                    isOpen={showBranchDrawer}
                    onClose={() => setShowBranchDrawer(false)}
                    title="Select Branches"
                    searchTerm={branchSearchTerm}
                    onSearchChange={(e) => setBranchSearchTerm(e.target.value)}
                    filterItems={filteredBranches}
                    selectedItems={selectedBranches}
                    onItemToggle={handleBranchFilterChange}
                    searchPlaceholder="Search branches..."
                  />

                  {/* Active filters tags */}
                  <div className="flex flex-wrap gap-2">
                    {selectedPlaces.map((place) => (
                      <div
                        key={place}
                        className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-md flex items-center gap-1"
                      >
                        <span className="truncate max-w-[120px]">{place}</span>
                        <button
                          onClick={() => handlePlaceFilterChange(place)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}

                    {selectedBranches.map((branch) => (
                      <div
                        key={branch}
                        className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded-md flex items-center gap-1"
                      >
                        <span className="truncate max-w-[120px]">{branch}</span>
                        <button
                          onClick={() => handleBranchFilterChange(branch)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Results table with fixed height and scrolling */}
                <div>
                  {/* Desktop view - Replaced with component */}
                  <DesktopResults
                    filteredColleges={filteredColleges}
                    rankKey={rankKey}
                    formData={formData}
                  />

                  {/* Mobile view - Card format remains unchanged */}
                  <MobileResults
                    filteredColleges={filteredColleges}
                    rankKey={rankKey}
                    formData={formData}
                    classifyRisk={classifyRisk}
                  />
                </div>

                {/* Result count */}
                <div className="mt-4 text-center text-gray-600 text-sm md:hidden">
                  {filteredColleges.length === colleges.length
                    ? `Showing all ${colleges.length} colleges`
                    : `Showing ${filteredColleges.length} of ${colleges.length} colleges`}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Feature Highlights Section */}
      {!formSubmitted && <WhyUs />}

      <OtherLinks/>
    </div>
  );
};

export default Home;
